/**
 * Activity Management Page (Refactored)
 * Create and manage learning activities
 *
 * Refactored from 358 LOC to component-based architecture
 */

import React, { useState } from 'react';
import { BookOpen } from 'lucide-react';
import { useChildren } from '../../hooks/useChildren';
import { useActivities } from '../../hooks/useActivities';
import { useAIActivities } from '../../hooks/useAIActivities';
import { useChildStats } from '../../hooks/useChildStats';
import { useActivityResults } from '../../hooks/useActivityResults';
import { useTheme } from '../../hooks/useTheme';
import { AIActivityGenerator } from '../../components/AIActivityGenerator';
import { AIActivityWizard } from '../../components/AIActivityWizard';
import { ActivityCard } from '../../components/ActivityCard';
import { AIActivityCard } from '../../components/AIActivityCard';
import { ActivityContentViewer } from '../../components/ActivityContentViewer';

// Extracted components
import { ChildSelector } from './components/ChildSelector';
import { ActivityFilter } from './components/ActivityFilter';
import { AIGeneratorCTA } from './components/AIGeneratorCTA';

type FilterOption = 'all' | 'completed' | 'not-completed';

export function ActivityManagementPage() {
  const { children } = useChildren();
  const { activities } = useActivities();
  const { isDarkMode } = useTheme();

  // State management
  const [selectedChild, setSelectedChild] = useState<string | null>(null);
  const [showAIGenerator, setShowAIGenerator] = useState(false);
  const [showWizard, setShowWizard] = useState(false);
  const [completedFilter, setCompletedFilter] = useState<FilterOption>('all');
  const [viewingActivity, setViewingActivity] = useState<{ activity: any; isAI: boolean } | null>(null);

  // Data hooks
  const { aiActivities, refetch: refetchAIActivities } = useAIActivities(selectedChild);
  const { stats } = useChildStats(selectedChild);
  const { getActivityResults } = useActivityResults();

  const selectedChildData = children.find(child => child.id === selectedChild);

  // Event handlers
  const handleAIActivityGenerated = async () => {
    setShowAIGenerator(false);
    await refetchAIActivities();
  };

  const handleWizardComplete = async () => {
    setShowWizard(false);
    await refetchAIActivities();
  };

  const handleViewActivity = async (activity: { id: string; [key: string]: any }, isAI: boolean = false) => {
    const results = await getActivityResults(activity.id, selectedChild);
    setViewingActivity({ activity: { ...activity, results }, isAI });
  };

  // Filter logic
  const filteredActivities = React.useMemo(() => {
    if (!selectedChild) return activities;

    switch (completedFilter) {
      case 'completed':
        return activities.filter(activity => stats.completedActivityIds.includes(activity.id));
      case 'not-completed':
        return activities.filter(activity => !stats.completedActivityIds.includes(activity.id));
      default:
        return activities;
    }
  }, [activities, selectedChild, completedFilter, stats.completedActivityIds]);

  const filteredAIActivities = React.useMemo(() => {
    if (!selectedChild) return aiActivities;

    switch (completedFilter) {
      case 'completed':
        return aiActivities.filter(activity => activity.status === 'completed');
      case 'not-completed':
        return aiActivities.filter(activity => activity.status === 'active');
      default:
        return aiActivities;
    }
  }, [aiActivities, selectedChild, completedFilter]);

  // Completion counts
  const completedCounts = React.useMemo(() => {
    if (!selectedChild) return { regular: 0, ai: 0 };

    const regularCompleted = activities.filter(activity =>
      stats.completedActivityIds.includes(activity.id)
    ).length;

    const aiCompleted = aiActivities.filter(activity =>
      activity.status === 'completed'
    ).length;

    return { regular: regularCompleted, ai: aiCompleted };
  }, [selectedChild, activities, aiActivities, stats.completedActivityIds]);

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className={`text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          Activity Management
        </h1>
        <p className={`mt-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
          Create and manage learning activities for your children
        </p>
      </div>

      {/* Child Selection */}
      <ChildSelector
        children={children}
        selectedChild={selectedChild}
        onSelectChild={setSelectedChild}
        isDarkMode={isDarkMode}
      />

      {/* Filter Controls */}
      {selectedChild && selectedChildData && (
        <ActivityFilter
          selectedFilter={completedFilter}
          onFilterChange={setCompletedFilter}
          childName={selectedChildData.name}
          regularCompleted={completedCounts.regular}
          regularTotal={activities.length}
          aiCompleted={completedCounts.ai}
          aiTotal={aiActivities.length}
          isDarkMode={isDarkMode}
        />
      )}

      {/* AI Generator CTA */}
      {selectedChild && selectedChildData && (
        <AIGeneratorCTA
          childName={selectedChildData.name}
          onSingleActivity={() => setShowAIGenerator(true)}
          onBatchWizard={() => setShowWizard(true)}
        />
      )}

      {/* AI Generated Activities */}
      {selectedChild && filteredAIActivities.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              AI-Generated Activities for {selectedChildData?.name}
            </h3>
            <div className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Showing {filteredAIActivities.length} of {aiActivities.length} AI activities
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredAIActivities.map((activity) => (
              <AIActivityCard
                key={activity.id}
                activity={activity}
                onViewContents={() => handleViewActivity(activity, true)}
                completed={activity.status === 'completed'}
                showViewContents={true}
              />
            ))}
          </div>
        </div>
      )}

      {/* Standard Activities */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            Standard Learning Activities
          </h3>
          {selectedChild && (
            <div className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Showing {filteredActivities.length} of {activities.length} activities
            </div>
          )}
        </div>

        {filteredActivities.length === 0 && selectedChild ? (
          <div className="text-center py-16">
            <div className={`w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6 ${
              isDarkMode ? 'bg-gray-700' : 'bg-gray-100'
            }`}>
              <BookOpen className={`w-12 h-12 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
            </div>
            <h2 className={`text-2xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              No {completedFilter === 'completed' ? 'Completed' : completedFilter === 'not-completed' ? 'Uncompleted' : ''} Activities
            </h2>
            <p className={`mb-8 max-w-md mx-auto ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              {completedFilter === 'completed'
                ? `${selectedChildData?.name} hasn't completed any activities yet.`
                : completedFilter === 'not-completed'
                ? `${selectedChildData?.name} has completed all available activities! Great job!`
                : 'No activities available.'
              }
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredActivities.slice(0, 12).map((activity) => (
              <ActivityCard
                key={activity.id}
                activity={activity}
                onViewContents={() => handleViewActivity(activity, false)}
                completed={selectedChild ? stats.completedActivityIds.includes(activity.id) : false}
                showViewContents={true}
              />
            ))}
          </div>
        )}
      </div>

      {/* Empty State - No Children */}
      {children.length === 0 && (
        <div className="text-center py-16">
          <div className={`w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6 ${
            isDarkMode ? 'bg-gray-700' : 'bg-gray-100'
          }`}>
            <BookOpen className={`w-12 h-12 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
          </div>
          <h2 className={`text-2xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            No Children Added
          </h2>
          <p className={`mb-8 max-w-md mx-auto ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            Add children first to start creating and managing their learning activities.
          </p>
        </div>
      )}

      {/* Modals */}
      {showAIGenerator && selectedChildData && (
        <AIActivityGenerator
          child={selectedChildData}
          onActivityGenerated={handleAIActivityGenerated}
          onClose={() => setShowAIGenerator(false)}
        />
      )}

      {showWizard && selectedChildData && (
        <AIActivityWizard
          mode="existing-child"
          initialChild={selectedChildData}
          onClose={handleWizardComplete}
        />
      )}

      {viewingActivity && (
        <ActivityContentViewer
          activity={viewingActivity.activity}
          isAIActivity={viewingActivity.isAI}
          progressRecords={viewingActivity.activity.results || []}
          onClose={() => setViewingActivity(null)}
        />
      )}
    </div>
  );
}
